// Multi-year demand projection, load uncertainty and metered profile import.
// Mirrors the backend twins:
//   BESS_feasibility/Configuration/demand_projection.py
//   BESS_feasibility/Configuration/load_profile.py
// Keep the key names, enum values and defaults in sync.
//
// Savings do not scale linearly with consumption: self-consumption saturates
// once the generation and the battery are fully used, while the capacity charge
// keeps rising with the peak. The engine therefore re-dispatches at several load
// levels and interpolates, rather than scaling year 1 — which is why growth and
// uncertainty are dispatch inputs, not report inputs.

import { readLoadProfile } from "./loadProfileImport";

export const DEFAULT_DEMAND_PROJECTION = {
  growthPctYear: "",
  steps: [],
  uncertaintyPct: "",
  redispatchGranularity: "anchors", // anchors | all | none
};

export const REDISPATCH_GRANULARITY_OPTIONS = [
  {
    value: "anchors",
    label: "Anchor years (recommended)",
    description:
      "Re-dispatch at years 1, 5, 10, 15 and 20 and interpolate the years in " +
      "between. Captures the saturation curve at a fraction of the runtime.",
  },
  {
    value: "all",
    label: "Every year",
    description:
      "Re-dispatch all twenty years. Most precise and much slower — each year " +
      "is a full 365-day optimisation.",
  },
  {
    value: "none",
    label: "Hold load flat",
    description:
      "Do not project load growth at all; only the retail price escalates. " +
      "The conservative choice when the site's future consumption is unknown.",
  },
];

export const GROWTH_HELPER_TEXT =
  "Expected annual change in the site's consumption, compounding over the " +
  "assessment period. Leave blank or 0 to hold consumption flat. This is a " +
  "dispatch input, not a report multiplier: the system is actually re-simulated " +
  "at the higher load levels, so saturating self-consumption and a rising " +
  "capacity charge both show up.";

export const STEPS_HELPER_TEXT =
  "Discrete changes in consumption from a given year onward — a new production " +
  "line, an extension, a shift pattern change, or electrification of heat or " +
  "vehicles. Applied on top of the growth trend.";

export const UNCERTAINTY_HELPER_TEXT =
  "How much the real consumption could differ from what has been entered. The " +
  "system is dispatched at both ends of the band, giving a P90 (low load) and " +
  "P10 (high load) saving alongside the base case. Leave blank for a single " +
  "deterministic result.";

export const LOAD_PROFILE_HELPER_TEXT =
  "Import a full year of metered consumption (hourly or 15-minute) to replace " +
  "the modelled profile. This is the single biggest accuracy improvement " +
  "available: the modelled profile reproduces the right annual energy but not " +
  "the short sharp maxima a real meter records, and the capacity charge is " +
  "billed on a maximum.";

export const HOURS_NON_LEAP = 8760;
export const HOURS_LEAP = 8784;
export const STEPS_15MIN_NON_LEAP = HOURS_NON_LEAP * 4;
export const STEPS_15MIN_LEAP = HOURS_LEAP * 4;

export function detectProfileResolution(count) {
  if (count === HOURS_NON_LEAP || count === HOURS_LEAP) return "hourly";
  if (count === STEPS_15MIN_NON_LEAP || count === STEPS_15MIN_LEAP) return "15min";
  return null;
}

// Default operating hours, matching what the schedule selects offer. "None"
// means closed, and is a deliberate answer rather than a missing one.
export const DEFAULT_WORKDAY_SCHEDULE = {
  weekdays: { startHour: "09:00", endHour: "17:00" },
  saturdays: { startHour: "10:00", endHour: "14:00" },
  sundays: { startHour: "None", endHour: "None" },
};

/**
 * Repair a schedule whose values were never persisted.
 *
 * The schedule selects used to display a default hour while storing an empty
 * string, so a user who never opened a dropdown saved a site that is open zero
 * hours a week — and the engine then generated a year of zero load. Only
 * EMPTY entries are filled: "None" is an explicit "closed" and is left alone,
 * as is any hour the user actually chose.
 */
export function repairDemandSchedule(schedule) {
  const current = schedule && typeof schedule === "object" ? schedule : {};
  const isUnset = (value) =>
    value === undefined || value === null || String(value).trim() === "";
  const repaired = {};
  let changed = false;
  Object.entries(DEFAULT_WORKDAY_SCHEDULE).forEach(([day, defaults]) => {
    const block = current[day] && typeof current[day] === "object" ? current[day] : {};
    const startHour = isUnset(block.startHour) ? defaults.startHour : block.startHour;
    const endHour = isUnset(block.endHour) ? defaults.endHour : block.endHour;
    if (startHour !== block.startHour || endHour !== block.endHour) changed = true;
    repaired[day] = { ...block, startHour, endHour };
  });
  // Preserve any day key we do not know about.
  Object.keys(current).forEach((day) => {
    if (!(day in repaired)) repaired[day] = current[day];
  });
  return { schedule: repaired, changed };
}

export function normalizeDemandProjection(cfg) {
  const raw =
    (cfg && cfg.feasibility && cfg.feasibility.technical &&
      cfg.feasibility.technical.demandProjection) ||
    (cfg && cfg.demandProjection) ||
    {};
  const granularity = REDISPATCH_GRANULARITY_OPTIONS.some(
    (o) => o.value === raw.redispatchGranularity
  )
    ? raw.redispatchGranularity
    : DEFAULT_DEMAND_PROJECTION.redispatchGranularity;
  return {
    ...DEFAULT_DEMAND_PROJECTION,
    ...raw,
    steps: Array.isArray(raw.steps) ? raw.steps : [],
    redispatchGranularity: granularity,
  };
}

/** Load scale factor per project year (year 1 = 1.0). Mirrors annual_load_factors. */
export function annualLoadFactors(projectYears, growthPctYear, steps) {
  const years = Math.max(0, Number(projectYears) || 0);
  const growth = (Number(growthPctYear) || 0) / 100;
  const list = Array.isArray(steps) ? steps : [];
  const factors = [];
  for (let year = 1; year <= years; year += 1) {
    let factor = (1 + growth) ** (year - 1);
    list.forEach((step) => {
      const stepYear = Number(step?.year);
      const delta = Number(step?.deltaPct);
      if (!Number.isFinite(stepYear) || !Number.isFinite(delta)) return;
      if (year >= stepYear) factor *= 1 + delta / 100;
    });
    factors.push(Math.max(0, factor));
  }
  return factors;
}

export function validateDemandProjection(projection, cfg) {
  const errors = {};
  if (!projection) return errors;

  const growth = Number(projection.growthPctYear);
  if (projection.growthPctYear !== "" && !Number.isFinite(growth)) {
    errors.growthPctYear = "Load growth must be a number (percent per year).";
  } else if (Math.abs(growth) > 25) {
    errors.growthPctYear =
      "Load growth beyond +/-25%/year compounds to an implausible level over the " +
      "assessment period. Use a step change for a one-off expansion instead.";
  }

  const uncertainty = Number(projection.uncertaintyPct);
  if (projection.uncertaintyPct !== "" && !Number.isFinite(uncertainty)) {
    errors.uncertaintyPct = "Load uncertainty must be a number (percent).";
  } else if (uncertainty < 0 || uncertainty > 50) {
    if (projection.uncertaintyPct !== "") {
      errors.uncertaintyPct = "Load uncertainty must be between 0 and 50%.";
    }
  }

  const horizon = Number(cfg?.feasibility?.settlementPeriod) || 0;
  (projection.steps || []).forEach((step, index) => {
    const year = Number(step?.year);
    const delta = Number(step?.deltaPct);
    if (!Number.isFinite(year) || year < 1 || (horizon && year > horizon)) {
      errors[`step_${index}_year`] = horizon
        ? `Step year must be between 1 and ${horizon}.`
        : "Step year must be 1 or later.";
    }
    if (!Number.isFinite(delta)) {
      errors[`step_${index}_delta`] = "Step change must be a number (percent).";
    } else if (delta <= -100) {
      errors[`step_${index}_delta`] =
        "A step change of -100% removes all consumption; use a smaller reduction.";
    }
  });

  return errors;
}

/** Advisory notes shown inline; mirrors the backend warning list. */
export function demandProjectionWarnings(projection, cfg) {
  const warnings = [];
  if (!projection) return warnings;
  const horizon = Number(cfg?.feasibility?.settlementPeriod) || 0;
  const growth = Number(projection.growthPctYear) || 0;

  if (horizon && growth) {
    const factors = annualLoadFactors(horizon, growth, projection.steps);
    const final = factors[factors.length - 1] || 1;
    warnings.push(
      `At ${growth}%/year, consumption in year ${horizon} is ` +
        `${Math.round(final * 100)}% of today's — ${final >= 2 ? "more than double. " : ""}` +
        "Confirm the site can actually grow that far within its connection capacity."
    );
  }
  if (projection.redispatchGranularity === "all" && horizon > 5) {
    warnings.push(
      `Re-dispatching every year runs the full optimisation ${horizon} times and ` +
        "will make this simulation substantially slower. Anchor years give " +
        "nearly the same answer."
    );
  }
  if (Number(projection.uncertaintyPct) > 0) {
    warnings.push(
      "The uncertainty band adds two more dispatch passes (one at each end), so " +
        "the run will take longer."
    );
  }
  return warnings;
}

export function buildDemandProjectionPayload(projection) {
  const normalized = normalizeDemandProjection({ demandProjection: projection });
  const num = (value) => {
    const parsed = Number(value);
    return Number.isFinite(parsed) ? parsed : 0;
  };
  return {
    growthPctYear: num(normalized.growthPctYear),
    uncertaintyPct: Math.abs(num(normalized.uncertaintyPct)),
    redispatchGranularity: normalized.redispatchGranularity,
    steps: (normalized.steps || [])
      .filter((step) => Number.isFinite(Number(step?.year)) && Number.isFinite(Number(step?.deltaPct)))
      .map((step) => ({
        year: Math.max(1, Math.round(Number(step.year))),
        deltaPct: num(step.deltaPct),
        label: String(step.label || "").trim(),
      })),
  };
}

// Read-only summary of an imported profile, for display beside the modelled
// inputs it replaces.
//
// Deliberately NOT written back into those inputs. Auto-filling them would
// overwrite what the user typed with derived data, and — worse — leave those
// derived values behind if the profile is later removed, silently treated as
// the user's own entry. Showing them separately gives the same sanity check
// with none of that risk.
const SUMMARY_MONTHS = [
  "January", "February", "March", "April", "May", "June",
  "July", "August", "September", "October", "November", "December",
];
const DAYS_NON_LEAP = [31, 28, 31, 30, 31, 30, 31, 31, 30, 31, 30, 31];
const DAYS_LEAP = [31, 29, 31, 30, 31, 30, 31, 31, 30, 31, 30, 31];

export function profileMonthlySummary(profile) {
  const raw = Array.isArray(profile?.values) ? profile.values : [];
  if (!raw.length) return null;

  const numeric = raw.map((v) => {
    const n = Number(v);
    return Number.isFinite(n) ? Math.max(0, n) : 0;
  });

  // Match the backend exactly: 15-minute data is SUMMED in groups of four,
  // because the values are energy per interval, not instantaneous power.
  const resolution =
    profile.resolution === "15min" || profile.resolution === "hourly"
      ? profile.resolution
      : detectProfileResolution(numeric.length);
  const hourly =
    resolution === "15min"
      ? Array.from({ length: Math.floor(numeric.length / 4) }, (_, i) =>
          numeric.slice(i * 4, i * 4 + 4).reduce((a, b) => a + b, 0)
        )
      : numeric;

  if (!hourly.length) return null;

  const days = hourly.length >= HOURS_LEAP ? DAYS_LEAP : DAYS_NON_LEAP;
  const perMonth = [];
  let cursor = 0;
  for (let m = 0; m < 12; m += 1) {
    const hours = days[m] * 24;
    const slice = hourly.slice(cursor, cursor + hours);
    perMonth.push({
      month: SUMMARY_MONTHS[m],
      kwh: slice.reduce((a, b) => a + b, 0),
      peakKw: slice.length ? Math.max(...slice) : 0,
    });
    cursor += hours;
  }

  const annualKwh = hourly.reduce((a, b) => a + b, 0);
  const peakKw = Math.max(...hourly);
  return {
    perMonth,
    annualKwh,
    peakKw,
    loadFactorPct: peakKw > 0 ? (annualKwh / (peakKw * hourly.length)) * 100 : 0,
    hours: hourly.length,
    resolution: resolution || "hourly",
    // A profile shorter or longer than the simulated year is fitted by the
    // backend; flag it so the reader knows the figures are the file's, not
    // necessarily the simulated year's.
    partial: hourly.length !== HOURS_NON_LEAP && hourly.length !== HOURS_LEAP,
  };
}

export function parseLoadProfileText(text, options = {}) {
  // Kept for callers of the old API. The reading is done by
  // loadProfileImport.readLoadProfile, which reads dates, decimal commas and
  // Greek Excel files; errors come back in English here.
  const r = readLoadProfile(text, options);
  return {
    values: r.values,
    resolution: r.resolution,
    errors: r.errors.map((m) => m.en),
    skippedLines: r.skippedLines || 0,
    count: r.values.length,
    annualKwh: r.annualKwh || 0,
    peakKw: r.peakKw || 0,
    nativePeakKw: r.nativePeakKw || 0,
    wasAggregated: Boolean(r.wasAggregated),
    peakSmoothingPct: r.peakSmoothingPct || 0,
    loadFactorPct: r.loadFactorPct || 0,
  };
}

// Seasons as the engine defines them (season_months in
// BESS_feasibility/Consumption/translate_demand.py). Kept identical so a shape
// read off this chart is the shape the study actually dispatches.
export const PROFILE_SEASONS = [
  { key: "winter", months: [12, 1, 2] },
  { key: "spring", months: [3, 4, 5] },
  { key: "summer", months: [6, 7, 8] },
  { key: "autumn", months: [9, 10, 11] },
];

/**
 * The mean day of an imported profile: 24 hourly averages for the whole year
 * and for each season.
 *
 * Averages rather than totals, because the four seasons do not hold the same
 * number of days and a total would make winter look bigger for that reason
 * alone. A season with no data in the file returns null rather than a flat
 * line of zeros, which would read as "the site consumes nothing in summer".
 */
export function profileDailyShape(profile) {
  const raw = Array.isArray(profile?.values) ? profile.values : [];
  if (!raw.length) return null;

  const numeric = raw.map((v) => {
    const n = Number(v);
    return Number.isFinite(n) ? Math.max(0, n) : 0;
  });
  const resolution =
    profile.resolution === "15min" || profile.resolution === "hourly"
      ? profile.resolution
      : detectProfileResolution(numeric.length);
  // Summed in fours, not averaged: the values are energy per interval.
  const hourly =
    resolution === "15min"
      ? Array.from({ length: Math.floor(numeric.length / 4) }, (_, i) =>
          numeric.slice(i * 4, i * 4 + 4).reduce((a, b) => a + b, 0)
        )
      : numeric;
  if (hourly.length < 24) return null;

  const days = hourly.length >= HOURS_LEAP ? DAYS_LEAP : DAYS_NON_LEAP;
  const monthOfHour = [];
  days.forEach((count, index) => {
    for (let h = 0; h < count * 24; h += 1) monthOfHour.push(index + 1);
  });

  const blank = () => ({ sum: new Array(24).fill(0), n: new Array(24).fill(0) });
  const year = blank();
  const bySeason = new Map(PROFILE_SEASONS.map((s) => [s.key, blank()]));
  const seasonOfMonth = new Map();
  PROFILE_SEASONS.forEach((s) => s.months.forEach((m) => seasonOfMonth.set(m, s.key)));

  hourly.forEach((value, index) => {
    const hour = index % 24;
    year.sum[hour] += value;
    year.n[hour] += 1;
    // A file longer than the calendar has no month for its tail; it is counted
    // in the year average and left out of the seasons rather than guessed at.
    const season = seasonOfMonth.get(monthOfHour[index]);
    if (!season) return;
    const bucket = bySeason.get(season);
    bucket.sum[hour] += value;
    bucket.n[hour] += 1;
  });

  const mean = (bucket) =>
    bucket.n.some((n) => n > 0)
      ? bucket.sum.map((total, h) => (bucket.n[h] ? total / bucket.n[h] : 0))
      : null;

  return {
    hours: Array.from({ length: 24 }, (_, h) => h),
    year: mean(year),
    seasons: PROFILE_SEASONS.map((s) => ({ key: s.key, values: mean(bySeason.get(s.key)) })),
  };
}
